import React, { useEffect, useState } from "react";
import { Box, Button, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

type CalendarView = "day" | "month" | "year";

interface DashboardCalendarPickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

const headerBtnSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  color: "#111827",
  textTransform: "none" as const,
  minWidth: 0,
  px: 0.75,
  py: 0.25,
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#fff1f1",
    color: "#d2363a",
  },
};

export const DashboardCalendarPicker: React.FC<DashboardCalendarPickerProps> = ({
  selected,
  onChange,
  minDate,
  maxDate,
}) => {
  const [view, setView] = useState<CalendarView>("day");
  const [cursorDate, setCursorDate] = useState<Date>(selected || new Date());

  useEffect(() => {
    if (selected) setCursorDate(selected);
  }, [selected]);

  return (
    <Box
      sx={{
        "& .react-datepicker": {
          border: "1px solid #d4c8c9",
          borderRadius: "10px",
          fontFamily: "'Poppins', sans-serif",
          boxShadow: "none",
        },
        "& .react-datepicker__header": {
          backgroundColor: "#fafbfc",
          borderBottom: "1px solid #d4c8c9",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          paddingTop: "8px",
        },
        "& .react-datepicker__current-month, & .react-datepicker-year-header": {
          display: "none",
        },
        "& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected, & .react-datepicker__month-text--selected, & .react-datepicker__year-text--selected, & .react-datepicker__month-text--keyboard-selected, & .react-datepicker__year-text--keyboard-selected":
          {
            backgroundColor: "#d2363a !important",
            color: "#fff !important",
          },
        "& .react-datepicker__day:hover, & .react-datepicker__month-text:hover, & .react-datepicker__year-text:hover":
          {
            backgroundColor: "#fff1f1",
          },
        "& .react-datepicker__navigation": {
          display: "none",
        },
        "& .react-datepicker__day--outside-month": {
          visibility: "hidden",
          pointerEvents: "none",
        },
      }}
    >
      <DatePicker
        key={`${view}-${cursorDate.getFullYear()}-${cursorDate.getMonth()}`}
        selected={selected}
        openToDate={cursorDate}
        filterDate={(date) => {
          if (view !== "day") return true;
          return (
            date.getMonth() === cursorDate.getMonth() &&
            date.getFullYear() === cursorDate.getFullYear()
          );
        }}
        onChange={(date: Date | null) => {
          if (!date) {
            onChange(null);
            return;
          }

          if (view === "year") {
            const next = new Date(cursorDate);
            next.setFullYear(date.getFullYear());
            setCursorDate(next);
            setView("month");
            return;
          }

          if (view === "month") {
            const next = new Date(cursorDate);
            next.setFullYear(date.getFullYear());
            next.setMonth(date.getMonth());
            // Keep day if possible, clamp later via selected update
            if (selected) next.setDate(Math.min(selected.getDate(), 28));
            setCursorDate(next);
            setView("day");
            return;
          }

          onChange(date);
          setCursorDate(date);
        }}
        inline
        minDate={minDate}
        maxDate={maxDate}
        showMonthYearPicker={view === "month"}
        showYearPicker={view === "year"}
        yearItemNumber={12}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          decreaseYear,
          increaseYear,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
          prevYearButtonDisabled,
          nextYearButtonDisabled,
        }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 0.5,
              pb: 0.5,
              gap: 0.5,
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                if (view === "year") {
                  decreaseYear();
                  const next = new Date(cursorDate);
                  next.setFullYear(cursorDate.getFullYear() - 12);
                  setCursorDate(next);
                } else if (view === "month") {
                  const next = new Date(cursorDate);
                  next.setFullYear(cursorDate.getFullYear() - 1);
                  setCursorDate(next);
                  decreaseYear();
                } else {
                  decreaseMonth();
                  const next = new Date(cursorDate);
                  next.setMonth(cursorDate.getMonth() - 1);
                  setCursorDate(next);
                }
              }}
              disabled={
                view === "day"
                  ? prevMonthButtonDisabled
                  : prevYearButtonDisabled
              }
              sx={{ color: "#6b7280" }}
            >
              <ChevronLeft size={16} />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              {view === "day" ? (
                <>
                  <Button
                    disableRipple
                    onClick={() => setView("month")}
                    sx={headerBtnSx}
                  >
                    {format(date, "MMMM")}
                  </Button>
                  <Button
                    disableRipple
                    onClick={() => setView("year")}
                    sx={headerBtnSx}
                  >
                    {format(date, "yyyy")}
                  </Button>
                </>
              ) : view === "month" ? (
                <Button
                  disableRipple
                  onClick={() => setView("year")}
                  sx={headerBtnSx}
                >
                  {format(date, "yyyy")}
                </Button>
              ) : (
                <Button
                  disableRipple
                  onClick={() => setView("day")}
                  sx={headerBtnSx}
                >
                  Select year
                </Button>
              )}
            </Box>

            <IconButton
              size="small"
              onClick={() => {
                if (view === "year") {
                  increaseYear();
                  const next = new Date(cursorDate);
                  next.setFullYear(cursorDate.getFullYear() + 12);
                  setCursorDate(next);
                } else if (view === "month") {
                  const next = new Date(cursorDate);
                  next.setFullYear(cursorDate.getFullYear() + 1);
                  setCursorDate(next);
                  increaseYear();
                } else {
                  increaseMonth();
                  const next = new Date(cursorDate);
                  next.setMonth(cursorDate.getMonth() + 1);
                  setCursorDate(next);
                }
              }}
              disabled={
                view === "day"
                  ? nextMonthButtonDisabled
                  : nextYearButtonDisabled
              }
              sx={{ color: "#6b7280" }}
            >
              <ChevronRight size={16} />
            </IconButton>
          </Box>
        )}
      />
    </Box>
  );
};
